import {
  DocumentSaveInteractionType,
  IAudit,
  IAuditNode,
  IDoctor,
  IHospital,
  IInstitutionAudit,
  INursingHome,
  INursingService,
  IPharmacy,
} from '@alberta/konexi-shared';
import { HashGenerator } from '@alberta/swodoc-document-hash-generator';
import { Injectable } from '@angular/core';
import { AlertButton, AlertController, ModalController, ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { AuditViewModel } from 'src/app/business/swodoc/view-models/audit-view-model';
import { BaseComponentModel } from 'src/app/common/component-model/base-component-model';
import { AuditDto } from 'src/app/shared/models/swodoc/audit-dto.model';
import { ActionEvent, TrackerService } from 'src/app/shared/services/tracker.service';

import { TreeArray } from '@common/tree-array/tree-array';
import { ATTACHMENT_LOCAL_PREFIX } from '@services/attachment/attachment-service.interface';
import { SavedInstanceStateService } from '@services/saved-instance-state/saved-instance-state.service';
import { TenantConfigService } from '@services/tenant-config/tenant-config.service';
import { assignIn } from 'lodash';
import { InstitutionsService } from 'src/app/institution/master/institutions.service';
import { AuditNodeDto } from 'src/app/shared/models/swodoc/audit-node-dto.model';
import { TherapyprogressDataService } from 'src/app/shared/therapyprogress/therapyprogress-data.service';
import { WoundhistoryChartService } from 'src/app/shared/therapyprogress/woundhistory-chart.service';
import { v4 } from 'uuid';
import { map as trackedMap } from '../../../../common/mapper/mapper';
import { DocumentEmailService } from '../document-email/document-email.service';
import { CompleteDialogAction } from './complete-dialog-action.enum';
import { DocumentAttachmentService, IAttachmentWithData } from './document-attachment.service';

@Injectable()
export class DocumentEditService {
  private _hashGenerator: HashGenerator;
  private _documentAttachmentService: DocumentAttachmentService;
  /**
   * if either no component model is set or saveOnDismiss is set to false
   * changes will not be saved after the user completes sends the document
   **/
  private _componentModel: BaseComponentModel<any>;
  private _saveOnDismiss: boolean;

  /**
   * Constructor
   */
  constructor(
    private _alertCtrl: AlertController,
    private _translate: TranslateService,
    private _trackerService: TrackerService,
    private _toastController: ToastController,
    private _savedInstanceStateService: SavedInstanceStateService,
    private _tenantConfigService: TenantConfigService,
    private _documentEmailService: DocumentEmailService,
    private _therapyProgressDataService: TherapyprogressDataService,
    private _woundHistoryChartService: WoundhistoryChartService,
    private _modalCtrl: ModalController,
    private _institutionsService: InstitutionsService
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  public async init(
    componentModel: BaseComponentModel<any> | undefined,
    audit: IAudit,
    documentAttachmentService: DocumentAttachmentService,
    saveOnDismiss: boolean
  ) {
    this._componentModel = componentModel;
    this._documentAttachmentService = documentAttachmentService;
    await this._documentAttachmentService.initAttachments(audit);
    this._hashGenerator = new HashGenerator(documentAttachmentService);
    this._saveOnDismiss = saveOnDismiss;
  }

  public async checkHashOfDocument(audit: AuditViewModel): Promise<boolean> {
    if (audit.hash) {
      const hashIsSame = await this.hashIsCorrect(audit);
      if (!hashIsSame) {
        this._documentAttachmentService.removeSignatures();
        await this.showHashNotSameAlert();
        return false;
      }
    }
    return true;
  }

  public getImagesForNodeId(id: string): Partial<IAttachmentWithData>[] {
    return this._documentAttachmentService.getImagesForNodeId(id);
  }

  public addImage(image: Partial<IAttachmentWithData>): void {
    this._documentAttachmentService.addImage(image);
  }

  public removeImage(id: string): void {
    this._documentAttachmentService.removeImage(id);
  }

  public checkIfNodeHasImages(id: string): boolean {
    const images = this._documentAttachmentService.getImagesForNodeId(id);
    return Boolean(images.length);
  }

  public async generateHash(audit: IAudit): Promise<string> {
    return await this._hashGenerator.generate(audit);
  }

  public async hashIsCorrect(audit: IAudit): Promise<boolean> {
    const allHashes = Object.keys(audit.hash).map(key => audit.hash[key]);
    const allHashesSame = allHashes.every(hash => hash === allHashes[0]);
    if (!allHashesSame) {
      return false;
    }
    const newHash = await this.generateHash(audit);
    return newHash === allHashes[0];
  }

  public async saveAudit(
    audit: AuditViewModel,
    options: {
      saveImagesAtAudit: boolean;
      isNew: boolean;
      sendEmailOnComplete: boolean;
      isLinkedToInstitutionDocument?: boolean;
    }
  ): Promise<void> {
    if (!audit) {
      throw new Error('audit is undefined');
    }

    this._trackerService.trackPatientDocumentsSave(ActionEvent.Start);

    await this._savedInstanceStateService.clearAllStates();

    const saveInteractionType = await this._tenantConfigService.completeAuditsOnCreate();

    const isEntityTypeValidToShowDialog = audit.entityType !== 'prescription' && audit.entityType !== 'medicalOrder';
    const showDialog =
      saveInteractionType !== DocumentSaveInteractionType.NoPrompt && isEntityTypeValidToShowDialog && !audit.draft;

    if (showDialog) {
      const showSaveButton = saveInteractionType === DocumentSaveInteractionType.PromptForCompleteOrSave;

      const sendEditConfirmEvent = await this.showCompleteDialog({ showMessage: true, showSaveButton });

      switch (sendEditConfirmEvent) {
        case CompleteDialogAction.Complete:
          this._trackerService.trackPatientDocumentsCompletedOnSave();
          audit.completed = true;
          if (options.sendEmailOnComplete) {
            await this.sendEmailOnComplete(audit);
          }
          break;
        case CompleteDialogAction.Save:
          this._trackerService.trackPatientDocumentsNotCompletedOnSave();
          break;
        case CompleteDialogAction.Cancel:
          return;
      }
    }

    if (options.saveImagesAtAudit) {
      this._trackerService.trackPatientDocumentsSaveImagesAtAudit();
      audit.images = this._documentAttachmentService.signatureImages();
    } else {
      this._trackerService.trackPatientDocumentsSaveNoImagesAtAudit();
      audit.images = [];
    }

    // check for therapy progress node and add the data
    const therapyProgressNode = this.getTherapyProgressNode(audit);
    if (!!therapyProgressNode) {
      await this.setTherapyProgressData(audit, therapyProgressNode);
    }

    // Do not wait for removing and adding images here as it lead to long waiting
    // times for users that wanted to save their document.
    // Instead return the promise and handle in swodoc-details.
    // tslint:disable-next-line: no-floating-promises
    const attachmentPromise = this.saveAttachments(
      audit,
      !options.saveImagesAtAudit,
      options.isLinkedToInstitutionDocument ? options.isLinkedToInstitutionDocument : false
    );

    if (!this._componentModel || this._saveOnDismiss === false) {
      this._modalCtrl.dismiss({ uploadFinishedPromise: attachmentPromise, audit }).catch(error => console.error(error));
      return;
    }

    this._trackerService.trackDocumentEditServiceSaveAudit(ActionEvent.Start);
    if (!audit.completedUtc) {
      audit.completedUtc = new Date().toISOString();
    }
    if (options.isNew) {
      await this._componentModel.create(trackedMap(audit, new AuditDto()));
    } else {
      audit.updatedUtc = new Date().toUTCString();
      await this._componentModel.edit(audit, trackedMap(audit, new AuditDto()));
    }
    this._trackerService.trackDocumentEditServiceSaveAudit(ActionEvent.Done);

    this._trackerService.trackPatientDocumentsSave(ActionEvent.Done);

    if (audit.draft) {
      await this.showDraftToastMessage();
    }
    this._modalCtrl.dismiss({ uploadFinishedPromise: attachmentPromise, audit }).catch(error => console.error(error));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  private saveAttachments(
    audit: AuditViewModel,
    saveAllImagesToDB: boolean,
    isLinkedToInstitutionDocument = false
  ): Promise<any> {
    this._trackerService.trackDocumentEditServiceSaveAttachments();
    return this._documentAttachmentService.writeAttachmentToDb(audit, saveAllImagesToDB, isLinkedToInstitutionDocument);
  }

  private async setTherapyProgressData(audit: IAudit, therapyProgressNode: IAuditNode) {
    const data = await this._therapyProgressDataService.getTherapyProgressData(audit);
    const chartB64 = await this._woundHistoryChartService.getWoundChartBase64(data.woundChartData);
    this.addImage({
      _id: `${ATTACHMENT_LOCAL_PREFIX}${v4()}`,
      data: chartB64,
      metadata: { auditNodeId: therapyProgressNode.id, type: 103 }, // 103 = SwodocTherapyProgress
    } as Partial<IAttachmentWithData>);
    therapyProgressNode.responses = [
      {
        value: data.therapyImages,
      },
      {
        value: [data.woundChartData],
      },
    ];
  }

  private getTherapyProgressNode(audit: IAudit): IAuditNode {
    return assignIn(new TreeArray<AuditNodeDto>(), audit.auditNodes).findRec(
      node => node.nodeType_Id === 'therapy_progress'
    );
  }

  private async sendEmailOnComplete(audit: AuditViewModel | IInstitutionAudit) {
    this._trackerService.trackPatientDocumentsSendEmailOnComplete(ActionEvent.Start);

    let emailAddresses: string[] = [];
    if (this.isInstitutionAudit(audit)) {
      const institution = await this.getInstituition(audit);
      emailAddresses = (await this._documentEmailService.openEmailModalForInstitution(
        audit.institutionType,
        institution
      )) as string[];
    } else {
      emailAddresses = (await this._documentEmailService.openEmailModal(audit.patientId)) as string[];
    }

    if (emailAddresses?.length) {
      audit.emailAddresses = emailAddresses;
      this._trackerService.trackPatientDocumentsSendEmailAdressesSet();
    }
    this._trackerService.trackPatientDocumentsSendEmailOnComplete(ActionEvent.Done);
  }

  private isInstitutionAudit(audit: AuditViewModel | IInstitutionAudit): audit is IInstitutionAudit {
    return (
      (audit as IInstitutionAudit).institutionId !== undefined &&
      (audit as IInstitutionAudit).institutionType !== undefined
    );
  }

  private async getInstituition(
    audit: IInstitutionAudit
  ): Promise<IDoctor | IHospital | INursingHome | INursingService | IPharmacy> {
    switch (audit.institutionType) {
      case 'Doctor':
        return this._institutionsService.getDoctor((audit as IInstitutionAudit).institutionId);
      case 'Pharmacy':
      case 'NursingHome':
      case 'NursingService':
      case 'Hospital':
        return this._institutionsService.getInstitutionByStringType(audit.institutionId, audit.institutionType);
    }
  }

  private async showHashNotSameAlert() {
    const alert = await this._alertCtrl.create({
      header: this._translate.instant('components.swodoc.signatureNotValid'),
      message: this._translate.instant('components.swodoc.signauterNotValidTitle'),
      buttons: [
        {
          text: 'OK',
        },
      ],
    });
    await alert.present();
    await alert.onDidDismiss();
  }

  private async showCompleteDialog(params: {
    showMessage: boolean;
    showSaveButton: boolean;
  }): Promise<CompleteDialogAction> {
    return new Promise<CompleteDialogAction>(async resolve => {
      const buttons: AlertButton[] = params.showSaveButton
        ? [
            {
              text: this._translate.instant('components.swodoc.saveDialog.action.complete'),
              cssClass: 'primary',
              handler: () => resolve(CompleteDialogAction.Complete),
            },
            {
              text: this._translate.instant('components.swodoc.saveDialog.action.save'),
              cssClass: 'primary',
              handler: () => resolve(CompleteDialogAction.Save),
            },
            {
              text: this._translate.instant('components.swodoc.saveDialog.action.cancel'),
              role: 'cancel',
              cssClass: 'secondary',
              handler: () => resolve(CompleteDialogAction.Cancel),
            },
          ]
        : [
            {
              text: this._translate.instant('components.swodoc.saveDialog.action.complete'),
              cssClass: 'primary',
              handler: () => resolve(CompleteDialogAction.Complete),
            },
            {
              text: this._translate.instant('components.swodoc.saveDialog.action.cancel'),
              role: 'cancel',
              cssClass: 'secondary',
              handler: () => resolve(CompleteDialogAction.Cancel),
            },
          ];

      const alert = await this._alertCtrl.create({
        header: this._translate.instant('components.swodoc.saveDialog.title'),
        message: params.showMessage ? this._translate.instant('components.swodoc.saveDialog.message') : '',
        backdropDismiss: false,
        buttons,
      });
      await alert.present();
    });
  }

  private async showDraftToastMessage() {
    const toastMessage = await this._toastController.create({
      header: this._translate.instant('components.swodoc.draftSaved'),
      position: 'bottom',
      duration: 5000,
    });
    await toastMessage.present();
  }
}
